/**
 * 보관·파기(EA-54~EA-59, EA-63·EA-64) — 탭 「보존·파기」·「연 단위 보관」·「회원 로그 보관 파일」.
 *
 * 요구: R-XAD-06(`~/dev1/devnet-docs/_data/2026-09-29/planning-answers/05_확정요구사항.md` 2-5).
 * 근거: 원문 14(결제기록 보존기간 5년, 연간마다 보관, 무중단), 확인 40(Q-58 — 5년 지난 기록은
 * 승인 뒤 파기, 거래소 지금 방식), 확인 42(운영 DB 에 올해 + 작년).
 *
 * 계약: XM-76(보관 파일)·XM-77(정책)·XM-78(정책 바꾸기)·XM-79(대상 세기)·XM-80(실행 목록)·
 * XM-81(승인)·XM-82(거절)·XM-97(연 단위 보관 현황)·XM-98(한 해 보관)·XM-99(보관소 읽기).
 *
 * ⚠ **지우는 것은 승인 한 번뿐이다.** 정책을 켜도, 대상을 세어도 아무것도 지우지 않는다.
 * 세기(XM-79)는 그때 센 줄 수와 기준 시각을 가진 '승인 대기 실행'을 만들고, 사람이
 * 그 실행을 승인(XM-81)해야 기준 시각보다 오래된 기록을 지운다. 파기 실행 기록은 지우지
 * 않는다(계약 XM-81).
 *
 * 연 단위 보관(EA-63·EA-64 — 계약 XM-97 현황·XM-98 한 해 보관·XM-99 보관소 읽기, R-XB-26):
 * 운영 DB 에는 올해 + 작년(확인 42), 그 전 해는 서비스 중에 보관소(`archive` 스키마)로 옮긴다
 * (원문 14 '연간데이터 분리작업으로인해 거래소가 멈추면 안된다'). 모양은 거래소백엔드
 * `adminapi/records/ArchiveController`·`YearlyArchive`·`ArchiveGroups` 그대로. 옮기기는 지우지
 * 않는다 — 보관된 행의 파기는 위 승인 절차 그대로(`archive.<묶음>`).
 *
 * ⚠ **보존 햇수는 5 이상**(계약 XM-78 `retentionYears int ≥5`, 원문 14). 화면도 5 미만을
 * 보내지 않는다 — 서버도 400 으로 막는다.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  approveRetentionRun, archiveYear, getArchiveOverview, listArchivedRows, listRetentionPolicies,
  listRetentionRuns, listUserLogArchives, patchRetentionPolicy, previewRetention, rejectRetentionRun,
} from '@/api/admin'
import { useSession } from '@/auth/session'
import { setFlash } from '@/auth/pendingWrite'
import {
  AdminRef, Badge, Button, Card, Code, Field, Labeled, Maybe, Notice, NUM, Row, Select, Tabs, TD, TH,
  Time,
} from '@/components/ui'
import { Confirm, submitWrite, useWrite } from '@/components/Confirm'
import { QueryState } from '@/components/QueryState'
import { OffsetPager, PagePager } from '@/components/Pager'
import { Choice, FilterBar, useFilters } from '@/components/Filters'
import { formatTime } from '@/format'
import {
  ARCHIVE_RUN_STATUS_LABEL, RETENTION_RUN_STATUS_LABEL, type ArchiveGroup, type ArchiveOverview,
  type ArchiveRun, type RetentionPolicy, type RetentionRun,
} from '@/api/types'

/** 보존 햇수 하한(계약 XM-78, 원문 14 '보존기간은 5년'). */
export const MIN_RETENTION_YEARS = 5

const RUN_SIZE = 25

type Tab = 'retention' | 'years' | 'archives'

export function RecordsPage() {
  const { has } = useSession()
  const f = useFilters(NO_FILTERS)
  const tabs: { kind: Tab; label: string }[] = []
  if (has('retention.view')) tabs.push({ kind: 'retention', label: 'Retention & destruction' })
  if (has('retention.view')) tabs.push({ kind: 'years', label: 'Yearly archive' })
  if (has('archives.view')) tabs.push({ kind: 'archives', label: 'Member log archive files' })
  const [active, setActive] = useState<Tab>(tabs[0]?.kind ?? 'retention')

  function switchTo(kind: Tab) {
    if (kind === active) return
    f.resetPaging()
    setActive(kind)
  }

  return (
    <Card title="Retention & archives">
      <p className="mb-4 text-xs text-slate-500">
        Destruction of records past their retention period, and archive files. Destruction happens only after the
        targets are counted and the run is approved.
      </p>
      <Tabs tabs={tabs} active={active} onSelect={switchTo} />
      {active === 'retention' && <RetentionTab />}
      {active === 'years' && <YearsTab />}
      {active === 'archives' && <ArchivesTab />}
    </Card>
  )
}

const NO_FILTERS: readonly string[] = []

/* ── EA-54 보존·파기 탭 (XM-77 · XM-80) ──────────────────────────── */

const RUN_FILTERS = ['target'] as const

function RetentionTab() {
  const { has } = useSession()
  const policies = useQuery({ queryKey: ['retention-policies'], queryFn: listRetentionPolicies })
  const f = useFilters(RUN_FILTERS)
  const page = Math.floor(f.offset / RUN_SIZE)
  const params = { target: f.values['target'] || undefined, page, size: RUN_SIZE }
  const runs = useQuery({ queryKey: ['retention-runs', params], queryFn: () => listRetentionRuns(params) })
  const [editing, setEditing] = useState<RetentionPolicy | null>(null)
  const [counting, setCounting] = useState<RetentionPolicy | null>(null)
  const canEdit = has('retention.edit')
  const rows = policies.data ?? []
  const targetOptions = rows.map((p) => ({ value: p.target, label: p.target }))

  return (
    <>
      <h3 className="mb-2 text-sm font-semibold">Policies</h3>
      <QueryState isPending={policies.isPending} error={policies.error} empty={rows.length === 0}
        emptyText="There are no destruction policies.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Target</th><th className={TH}>Description</th><th className={TH}>Retention</th>
            <th className={TH}>Destruction</th><th className={TH}>Connection</th><th className={TH}>Changed at</th>
            <th className={TH}>Changed by</th>{canEdit && <th className={TH}>Actions</th>}
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.target}>
                <td className={TD}><Code>{p.target}</Code></td>
                <td className={TD}><Maybe value={p.description} /></td>
                <td className={TD}>{p.retentionYears} years</td>
                <td className={TD}>{p.enabled ? <Badge tone="warn">On</Badge> : 'Off'}</td>
                <td className={TD}>{p.connected ? 'Connected' : <span className="text-red-700 dark:text-red-400">Not connected</span>}</td>
                <td className={TD}><Time ms={p.updatedAt} /></td>
                <td className={TD}><AdminRef adminId={p.updatedByAdminId} /></td>
                {canEdit && (
                  <td className={TD}>
                    <div className="flex gap-2">
                      <Button tone="plain" onClick={() => setEditing(p)}>Change policy</Button>
                      {/* ⚠ 꺼졌거나 연결이 없으면 서버가 409·503 — 단추를 두지 않는다. */}
                      {p.enabled && p.connected && (
                        <Button tone="plain" onClick={() => setCounting(p)}>Count targets</Button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>

      <h3 className="mb-2 mt-6 text-sm font-semibold">Destruction runs</h3>
      <FilterBar active={f.active} onClear={f.clear}>
        <Choice label="Target" value={f.values['target'] ?? ''} options={targetOptions}
          onChange={(v) => f.set('target', v)} />
      </FilterBar>
      <QueryState isPending={runs.isPending} error={runs.error} empty={runs.data?.length === 0}
        emptyText="There are no destruction runs.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Target</th><th className={TH}>Retention</th>
            <th className={TH}>Cutoff</th><th className={TH}>Counted rows</th><th className={TH}>Status</th>
            <th className={TH}>Deleted rows</th><th className={TH}>Counted by</th><th className={TH}>Counted at</th>
            <th className={TH}>Decided by</th><th className={TH}>Decided at</th><th className={TH}>Actions</th>
          </tr></thead>
          <tbody>
            {(runs.data ?? []).map((r) => (
              <tr key={r.id}>
                <td className={TD}>{r.id}</td>
                <td className={TD}><Code>{r.target}</Code></td>
                <td className={TD}>{r.retentionYears} years</td>
                <td className={TD}><Time ms={r.cutoff} /></td>
                <td className={NUM}>{r.counted.toLocaleString('en-US')}</td>
                <td className={TD}>
                  <Labeled labels={RETENTION_RUN_STATUS_LABEL} code={r.status} />
                  {r.error && <div className="text-xs text-red-700 dark:text-red-400">{r.error}</div>}
                </td>
                <td className={NUM}><Maybe value={r.deletedCount === null ? null : r.deletedCount.toLocaleString('en-US')} /></td>
                <td className={TD}><AdminRef adminId={r.requestedByAdminId} /></td>
                <td className={TD}><Time ms={r.requestedAt} /></td>
                <td className={TD}><AdminRef adminId={r.decidedByAdminId} /></td>
                <td className={TD}><Time ms={r.decidedAt} /></td>
                <td className={TD}><RunButtons run={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
      <OffsetPager offset={page * RUN_SIZE} limit={RUN_SIZE} count={runs.data?.length} onChange={f.setOffset} />

      {editing && <PolicyDialog policy={editing} onClose={() => setEditing(null)} />}
      {counting && <PreviewDialog policy={counting} onClose={() => setCounting(null)} />}
    </>
  )
}

/**
 * 보존 햇수 입력 → 보낼 값. 5 이상 정수만(계약 XM-78). 막히면 null.
 * ⚠ 위 끝은 계약에 없다 — 서버가 400 으로 답하면 그 문구를 보인다.
 */
export function toRetentionYears(input: string): number | null {
  const s = input.trim()
  if (!/^\d{1,3}$/.test(s)) return null
  const n = Number(s)
  return n >= MIN_RETENTION_YEARS ? n : null
}

/** 바뀐 칸만(계약 XM-78). 바뀐 것이 없으면 null. */
export function policyPatch(policy: RetentionPolicy, enabled: boolean, years: number | null):
  { enabled?: boolean; retentionYears?: number } | null {
  const patch: { enabled?: boolean; retentionYears?: number } = {}
  if (enabled !== policy.enabled) patch.enabled = enabled
  if (years !== null && years !== policy.retentionYears) patch.retentionYears = years
  return Object.keys(patch).length === 0 ? null : patch
}

/** EA-55 — 파기 정책 바꾸기(XM-78). */
function PolicyDialog({ policy, onClose }: { policy: RetentionPolicy; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()
  const [enabled, setEnabled] = useState(policy.enabled)
  const [yearsText, setYearsText] = useState(String(policy.retentionYears))
  const years = toRetentionYears(yearsText)
  const patch = policyPatch(policy, enabled, years)

  async function submit() {
    if (patch === null) return
    const send = async () => {
      await patchRetentionPolicy(policy.target, patch)
      setFlash({ tone: 'ok', text: `Changed the destruction policy ${policy.target}.` })
      await qc.invalidateQueries({ queryKey: ['retention-policies'] })
    }
    await submitWrite(w, 'Change destruction policy', send, onClose)
  }

  return (
    <Confirm open title={`Destruction policy ${policy.target}`} action="Apply"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}
      disabled={years === null || patch === null}>
      <div className="flex flex-col gap-3">
        <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
          <Row label="Description"><Maybe value={policy.description} /></Row>
          <Row label="Now">{policy.retentionYears} years · {policy.enabled ? 'On' : 'Off'}</Row>
        </dl>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span>Turn on destruction</span>
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium">Retention years ({MIN_RETENTION_YEARS} or more)</span>
          <span className="flex items-center gap-2">
            <input value={yearsText} inputMode="numeric" aria-label="Retention years"
              onChange={(e) => setYearsText(e.target.value)}
              className="w-24 rounded border border-slate-300 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-800" />
            <span>years</span>
          </span>
        </label>
        {years === null && (
          <p className="text-xs text-red-700 dark:text-red-400">Retention years is a whole number of {MIN_RETENTION_YEARS} or more.</p>
        )}
        <p className="text-xs text-slate-500">
          Turning it on alone deletes nothing. Records are deleted only after &quot;Count targets&quot; creates a run
          that waits for approval and that run is approved.
        </p>
      </div>
    </Confirm>
  )
}

/** EA-56 — 대상 세기(XM-79). 지우지 않는다 — 승인 대기 실행을 만든다. */
function PreviewDialog({ policy, onClose }: { policy: RetentionPolicy; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      const run = await previewRetention(policy.target)
      setFlash({
        tone: 'ok',
        text: `${policy.target}: counted ${run.counted.toLocaleString('en-US')} records older than ${formatTime(run.cutoff) ?? '—'}. Destruction run #${run.id} is waiting for approval.`,
      })
      await qc.invalidateQueries({ queryKey: ['retention-runs'] })
    }
    await submitWrite(w, 'Count destruction targets', send, onClose)
  }

  return (
    <Confirm open title={`Count targets — ${policy.target}`} action="Count"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <p>
        Counts the <Code>{policy.target}</Code> records older than <strong>{policy.retentionYears} years</strong>
        from now.
      </p>
      <p className="mt-2 text-xs text-slate-500">
        This only counts. The result is kept as a destruction run, and records are deleted only when it is
        approved. If a run is already waiting for approval, no new one is created.
      </p>
    </Confirm>
  )
}

function RunButtons({ run }: { run: RetentionRun }) {
  const { has } = useSession()
  const [mode, setMode] = useState<'approve' | 'reject' | null>(null)
  if (!has('retention.decide') || run.status !== 'PENDING_APPROVAL') return null
  return (
    <>
      <div className="flex gap-2">
        <Button onClick={() => setMode('approve')}>Approve</Button>
        <Button tone="plain" onClick={() => setMode('reject')}>Reject</Button>
      </div>
      {mode === 'approve' && <ApproveRunDialog run={run} onClose={() => setMode(null)} />}
      {mode === 'reject' && <RejectRunDialog run={run} onClose={() => setMode(null)} />}
    </>
  )
}

/** EA-57 — 파기 승인(XM-81). ⚠ 되돌릴 수 없다. */
function ApproveRunDialog({ run, onClose }: { run: RetentionRun; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      const done = await approveRetentionRun(run.id)
      setFlash({
        tone: 'ok',
        text: `Approved destruction run #${run.id}. Deleted rows: ${done.deletedCount === null ? '—' : done.deletedCount.toLocaleString('en-US')}.`,
      })
      await qc.invalidateQueries({ queryKey: ['retention-runs'] })
    }
    await submitWrite(w, 'Approve destruction', send, onClose, {
      // 실패(500)해도 서버가 실행을 '실패'로 남긴다 — 목록을 다시 읽는다.
      onError: () => { void qc.invalidateQueries({ queryKey: ['retention-runs'] }) },
    })
  }

  return (
    <Confirm open title={`Approve destruction — #${run.id}`} action="Destroy" danger
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <dl className="grid grid-cols-[6rem_1fr] gap-1.5">
        <Row label="Target"><Code>{run.target}</Code></Row>
        <Row label="Retention">{run.retentionYears} years</Row>
        <Row label="Cutoff"><Time ms={run.cutoff} /></Row>
        <Row label="Counted rows">{run.counted.toLocaleString('en-US')}</Row>
        <Row label="Counted by"><AdminRef adminId={run.requestedByAdminId} /></Row>
      </dl>
      <div className="mt-3">
        <Notice tone="warn">
          Deletes the records older than the cutoff. Deleted records cannot be restored. The cutoff stays as it
          was when counted, and the number of rows actually deleted is kept in &quot;Deleted rows&quot; of the run.
        </Notice>
      </div>
    </Confirm>
  )
}

/** EA-58 — 파기 거절(XM-82). */
function RejectRunDialog({ run, onClose }: { run: RetentionRun; onClose: () => void }) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      await rejectRetentionRun(run.id)
      setFlash({ tone: 'ok', text: `Rejected destruction run #${run.id}.` })
      await qc.invalidateQueries({ queryKey: ['retention-runs'] })
    }
    await submitWrite(w, 'Reject destruction', send, onClose)
  }

  return (
    <Confirm open title={`Reject destruction — #${run.id}`} action="Reject"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <p>
        Rejects destruction run #{run.id} of <Code>{run.target}</Code>. Nothing is deleted.
      </p>
    </Confirm>
  )
}

/* ── EA-63 연 단위 보관 탭 (XM-97 · XM-99) · EA-64 보관 실행 (XM-98) ─── */

/**
 * 보관할 해 입력 → 보낼 값. 네 자리 해이고 보관할 수 있는 마지막 해(`archivableThrough`,
 * 올해 − 2) 이하일 때만. 막히면 null. ⚠ 최종 판단은 서버(올해·작년 409, 2000~9999 밖 400 — 계약 XM-98).
 */
export function toArchiveYear(input: string, archivableThrough: number): number | null {
  const s = input.trim()
  if (!/^\d{4}$/.test(s)) return null
  const n = Number(s)
  return n >= 2000 && n <= archivableThrough ? n : null
}

/** XM-98 의 답(실행 한 건) → 알림. `FAILED` 도 200 으로 온다 — 실패는 빨간 알림. */
export function archiveFlash(run: ArchiveRun): { tone: 'ok' | 'stop'; text: string } {
  const moved = run.movedRows === null ? '—' : run.movedRows.toLocaleString('en-US')
  if (run.status === 'DONE') {
    return {
      tone: 'ok',
      text: `Moved the records of ${run.year} to the archive. Archive run #${run.runId}, moved rows ${moved}. The row count and md5 per table are in the archive run table.`,
    }
  }
  return {
    tone: 'stop',
    text: `Archive run #${run.runId} for ${run.year} ${run.status === 'FAILED' ? 'failed' : `ended as ${run.status}`}${run.error ? ` — ${run.error}` : ''}. From the failed group on, the records stay in the operational DB.`,
  }
}

/** 보관하는 표 이름(묶음 순서, 한 번씩 — XM-99 `table` 로 고를 수 있는 것). */
export function archivedTables(groups: readonly ArchiveGroup[]): string[] {
  return [...new Set(groups.flatMap((g) => g.tables))]
}

function YearsTab() {
  const { has } = useSession()
  const q = useQuery({ queryKey: ['archive-overview'], queryFn: getArchiveOverview })
  const o = q.data
  return (
    <>
      <p className="mb-2 text-xs text-slate-500">
        The operational DB keeps only this year's and last year's records. Earlier years are moved to the archive
        year by year without stopping the service. Moving does not delete — the archive can be read below, and
        destruction still goes through the retention and destruction approval.
      </p>
      <QueryState isPending={q.isPending} error={q.error}>
        {o && <YearsBody overview={o} canArchive={has('retention.edit')} />}
      </QueryState>
    </>
  )
}

function YearsBody({ overview: o, canArchive }: { overview: ArchiveOverview; canArchive: boolean }) {
  const [yearText, setYearText] = useState(String(o.archivableThrough))
  const [archiving, setArchiving] = useState<number | null>(null)
  const year = toArchiveYear(yearText, o.archivableThrough)

  return (
    <>
      <dl className="mb-4 grid grid-cols-[12rem_1fr] gap-1.5 text-sm">
        <Row label="Years in the operational DB">
          <strong>{o.operationalYears.length > 0 ? o.operationalYears.join(' · ') : '—'}</strong>
        </Row>
        <Row label="Last year that can be archived"><strong>{o.archivableThrough}</strong></Row>
      </dl>

      {/* ⚠ 키는 계약 XM-98 `retention.edit`. 올해·작년·진행 중은 서버가 409 — 최종 판단은 서버. */}
      {canArchive && (
        <div className="mb-4">
          <div className="flex items-end gap-2">
            <Field label={`Year to archive (up to ${o.archivableThrough})`} value={yearText} inputMode="numeric"
              onChange={(e) => setYearText(e.target.value)} />
            <Button onClick={() => { if (year !== null) setArchiving(year) }} disabled={year === null}>
              Run archive
            </Button>
          </div>
          {year === null && (
            <p className="mt-1 text-xs text-red-700 dark:text-red-400">
              The year to archive is a four-digit year up to {o.archivableThrough}.
            </p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            The daily job (02:30 UTC) also moves {o.archivableThrough} once, and runs again a year whose last run
            failed.
          </p>
        </div>
      )}

      <h3 className="mb-2 text-sm font-semibold">Archived records</h3>
      <table className="w-full text-sm">
        <thead><tr>
          <th className={TH}>Group</th><th className={TH}>Description</th><th className={TH}>Tables</th>
        </tr></thead>
        <tbody>
          {o.groups.map((g) => (
            <tr key={g.code}>
              <td className={TD}><Code>{g.code}</Code></td>
              <td className={TD}>{g.description}</td>
              <td className={`${TD} break-all font-mono text-xs`}>{g.tables.join(', ')}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 className="mb-2 mt-6 text-sm font-semibold">Archive runs</h3>
      {o.runs.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">There are no archive runs.</p>
      ) : (
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>No.</th><th className={TH}>Year</th><th className={TH}>Status</th>
            <th className={TH}>Started</th><th className={TH}>Finished</th><th className={TH}>Moved rows</th>
            <th className={TH}>Requested by</th><th className={TH}>Rows and md5 per table</th>
          </tr></thead>
          <tbody>
            {o.runs.map((r) => (
              <tr key={r.runId}>
                <td className={TD}>{r.runId}</td>
                <td className={TD}>{r.year}</td>
                <td className={TD}>
                  <Labeled labels={ARCHIVE_RUN_STATUS_LABEL} code={r.status} />
                  {r.error && <div className="break-all text-xs text-red-700 dark:text-red-400">{r.error}</div>}
                </td>
                <td className={TD}><Time ms={r.startedAt} /></td>
                <td className={TD}><Time ms={r.finishedAt} /></td>
                <td className={NUM}><Maybe value={r.movedRows === null ? null : r.movedRows.toLocaleString('en-US')} /></td>
                <td className={TD}><AdminRef adminId={r.requestedByAdminId} empty="Daily job" /></td>
                <td className={TD}><RunParts run={r} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h3 className="mb-2 mt-6 text-sm font-semibold">Read the archive</h3>
      <ArchivedRowsReader overview={o} />

      {archiving !== null && (
        <ArchiveDialog year={archiving} groups={o.groups} onClose={() => setArchiving(null)} />
      )}
    </>
  )
}

/** 실행의 표별 결과 — 묶음·표·옮긴 행·md5(옮기기 전 읽은 행과 같음을 확인한 값). */
function RunParts({ run }: { run: ArchiveRun }) {
  if (run.parts.length === 0) return <Maybe value={null} />
  return (
    <details>
      <summary className="cursor-pointer text-xs">{run.parts.length} tables</summary>
      <table className="mt-1 text-xs">
        <tbody>
          {run.parts.map((p) => (
            <tr key={`${p.group}-${p.partNo}`}>
              <td className="pr-2 font-mono">{p.group}</td>
              <td className="pr-2 font-mono">{p.table}</td>
              <td className="pr-2 text-right font-mono tabular-nums">{p.rows.toLocaleString('en-US')}</td>
              <td className="break-all font-mono"><Maybe value={p.checksum} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  )
}

/** EA-64 — 한 해 보관 실행(XM-98). 지우지 않는다 — 운영 표에서 보관소로 옮긴다. */
function ArchiveDialog({ year, groups, onClose }: {
  year: number
  groups: readonly ArchiveGroup[]
  onClose: () => void
}) {
  const qc = useQueryClient()
  const w = useWrite()

  async function submit() {
    const send = async () => {
      const run = await archiveYear(year)
      setFlash(archiveFlash(run))
      await qc.invalidateQueries({ queryKey: ['archive-overview'] })
    }
    await submitWrite(w, 'Yearly archive', send, onClose, {
      onError: () => { void qc.invalidateQueries({ queryKey: ['archive-overview'] }) },
    })
  }

  return (
    <Confirm open title={`Archive run for ${year}`} action="Run archive"
      busy={w.busy} error={w.error} onCancel={onClose} onConfirm={() => { void submit() }}>
      <p>
        Moves the records of <strong>{year}</strong> from the operational DB to the archive while the service runs.
      </p>
      <p className="mt-2 text-xs text-slate-500">
        Groups to move: {groups.map((g) => g.description).join(' · ')}
      </p>
      <p className="mt-2 text-xs text-slate-500">
        For each group, rows are taken out of the operational tables only when the count and md5 of the rows read
        and the rows moved match. If they differ, that group is rolled back and the run is kept as failed. Nothing
        is deleted. This year and last year cannot be moved.
      </p>
    </Confirm>
  )
}

/** 보관소 읽기 한 쪽 크기(계약 XM-99 기본 50, 1~200). */
const ARCHIVED_ROW_SIZE = 50

/** 읽을 해 입력 → 네 자리 해. 막히면 null. */
function toReadYear(input: string): number | null {
  const s = input.trim()
  return /^\d{4}$/.test(s) ? Number(s) : null
}

/** 보관소 읽기(XM-99) — 해와 표를 골라 「읽기」. 행은 보관된 그대로(열 이름 = DB 열 이름). */
function ArchivedRowsReader({ overview }: { overview: ArchiveOverview }) {
  const tables = archivedTables(overview.groups)
  const [yearText, setYearText] = useState(String(overview.archivableThrough))
  const [table, setTable] = useState(tables[0] ?? '')
  const [shown, setShown] = useState<{ year: number; table: string } | null>(null)
  const year = toReadYear(yearText)

  return (
    <>
      <div className="mb-3 flex flex-wrap items-end gap-2">
        <Field label="Year to read" value={yearText} inputMode="numeric" onChange={(e) => setYearText(e.target.value)} />
        <Select label="Table" value={table} onChange={setTable}
          options={tables.map((t) => ({ value: t, label: t }))} />
        <Button tone="plain" disabled={year === null || table === ''}
          onClick={() => { if (year !== null) setShown({ year, table }) }}>
          Read
        </Button>
      </div>
      {shown && <ArchivedRowsTable key={`${shown.year}-${shown.table}`} year={shown.year} table={shown.table} />}
    </>
  )
}

function cellText(v: unknown): string | null {
  if (v === null || v === undefined) return null
  return typeof v === 'object' ? JSON.stringify(v) : String(v)
}

function ArchivedRowsTable({ year, table }: { year: number; table: string }) {
  const [page, setPage] = useState(0)
  const q = useQuery({
    queryKey: ['archived-rows', year, table, page],
    queryFn: () => listArchivedRows(year, { table, page, size: ARCHIVED_ROW_SIZE }),
  })
  const rows = q.data?.rows ?? []
  const columns = [...new Set(rows.flatMap((r) => Object.keys(r)))]

  return (
    <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
      emptyText={`There are no archived rows of ${table} for ${year}.`}>
      <p className="mb-2 text-xs text-slate-500">
        {year} <Code>{table}</Code> archived rows: {(q.data?.total ?? 0).toLocaleString('en-US')}
      </p>
      <div className="overflow-x-auto">
        <table className="text-xs">
          <thead><tr>
            {columns.map((c) => <th key={c} className={`${TH} font-mono`}>{c}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                {columns.map((c) => (
                  <td key={c} className={`${TD} max-w-xs break-all font-mono`}><Maybe value={cellText(r[c])} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PagePager page={page} size={ARCHIVED_ROW_SIZE} total={q.data?.total} onChange={setPage} withPrev />
    </QueryState>
  )
}

/* ── EA-59 회원 로그 보관 파일 탭 (XM-76) ────────────────────────── */

function ArchivesTab() {
  const q = useQuery({ queryKey: ['user-log-archives'], queryFn: listUserLogArchives })
  const rows = q.data ?? []
  return (
    <>
      <p className="mb-4 text-xs text-slate-500">
        Archive files of member logs split by week. Check each file against its row count and hash.
      </p>
      <QueryState isPending={q.isPending} error={q.error} empty={rows.length === 0}
        emptyText="There are no archive files.">
        <table className="w-full text-sm">
          <thead><tr>
            <th className={TH}>Week start</th><th className={TH}>File</th><th className={TH}>Rows</th>
            <th className={TH}>Log no.</th><th className={TH}>SHA-256</th><th className={TH}>Created at</th>
          </tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.fileName}>
                <td className={TD}>{a.weekStart}</td>
                <td className={`${TD} break-all font-mono text-xs`}>{a.fileName}</td>
                <td className={NUM}>{a.rowCount.toLocaleString('en-US')}</td>
                <td className={`${TD} font-mono text-xs`}>
                  <Maybe value={a.firstLogId} /> ~ <Maybe value={a.lastLogId} />
                </td>
                <td className={`${TD} break-all font-mono text-xs`}>{a.sha256}</td>
                <td className={TD}><Time ms={a.createdAt} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </>
  )
}
